import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, Modifier, CompositeDecorator, convertFromHTML, ContentState } from 'draft-js';
import * as XLSX from 'xlsx';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './MetadataGrid.scss';
/* ─── Constants ─────────────────────────────────────────────────────────── */

const EDITOR_FIELDS = ['seo_meta_desc', 'seo_og_desc', 'seo_twitter_desc'];

const CHIP_FIELDS = [
    'seo_meta_title', 'seo_meta_tag', 'seo_meta_key_words', 'seo_alt_tag',
    'seo_og_image_path', 'seo_og_image_path_01', 'seo_og_image_path_02',
    'seo_og_image_path_03', 'seo_og_image_path_04', 'seo_og_image_path_05',
    'seo_og_title', 'seo_og_tag', 'seo_og_key_words',
    'seo_og_image_width', 'seo_og_image_height',
    'seo_twitter_title', 'seo_twitter_card', 'seo_twitter_site',
];

const EDITABLE_FIELDS = [...EDITOR_FIELDS, ...CHIP_FIELDS, 'seo_country'];

const READONLY_FIELDS = ['id', 'type', 'name', 'seo_id', 'seo_ref_id', 'lang_code', 'image_path', 'img_path', 'seo_active_yn', 'seo_ref_sys_id', 'SHP_FILE_PATH', 'SHP_DESC', 'SHP_SYS_ID'];

const COL_LABELS = {
    seo_ref_sys_id: 'SEO Ref Sys ID',
    id: 'ID', name: 'Name', type: 'Type', seo_id: 'SEO ID',
    seo_meta_title: 'Meta Title', seo_page_name: 'Page Name',
    seo_page_desc: 'Page Description', page_desc: 'Page Description',
    seo_meta_tag: 'Meta Tag', seo_alt_tag: 'Alt Tag',
    seo_meta_key_words: 'Meta Keywords', seo_meta_desc: 'Meta Description',
    seo_og_title: 'OG Title', seo_og_tag: 'OG Tag',
    seo_og_key_words: 'OG Keywords', seo_og_type: 'OG Type',
    seo_og_desc: 'OG Description', seo_og_image_width: 'OG Img Width',
    seo_og_image_height: 'OG Img Height', seo_twitter_title: 'Twitter Title',
    seo_twitter_desc: 'Twitter Desc', seo_twitter_card: 'Twitter Card',
    seo_twitter_site: 'Twitter Site', seo_active_yn: 'Active',
    lang_code: 'Lang', seo_country: 'Country',
    seo_og_image_path: 'OG Img Main',
    seo_og_image_path_01: 'OG Img 1', seo_og_image_path_02: 'OG Img 2',
    seo_og_image_path_03: 'OG Img 3', seo_og_image_path_04: 'OG Img 4',
    seo_og_image_path_05: 'OG Img 5',
    SHP_SYS_ID: 'SHP Sys ID',
    SHP_DESC: 'SHP Desc',
    SHP_FILE_PATH: 'Image',
};

const COL_ORDER = [
    'seo_ref_sys_id', 'seo_page_name', 'seo_country', 'seo_page_desc', 'page_desc', 'name', 'SHP_SYS_ID', 'SHP_DESC', 'SHP_FILE_PATH', 'seo_meta_title', 'seo_meta_tag', 'seo_alt_tag', 'seo_meta_key_words',
    'seo_meta_desc', 'seo_og_title', 'seo_og_tag', 'seo_og_key_words',
    'seo_og_desc', 'seo_og_image_path', 'seo_og_image_path_01', 'seo_og_image_path_02',
    'seo_og_image_path_03', 'seo_og_image_path_04', 'seo_og_image_path_05',
    'seo_og_image_width', 'seo_og_image_height', 'seo_twitter_title',
    'seo_twitter_desc', 'seo_twitter_card', 'seo_twitter_site', 'seo_active_yn', 'lang_code',
];

const SEO_FOR_MAP = { page: 'content', product: 'productinfo', item: 'iteminfo', category: 'category' };

const DEFAULT_COL_WIDTH = 180;
const MIN_COL_WIDTH = 80;
const PAGE_SIZE = 20;

const SKIP_KEYS = ['id', 'type', 'image_path', 'img_path', 'label', 'value', 'originalData'];

/* ─── Draft.js Chip Decorator ────────────────────────────────────────────── */
const ChipSpan = (props) => (
    <span className="mg-editor-chip">
        {props.children}
    </span>
);

function findChipEntities(contentBlock, callback, contentState) {
    const text = contentBlock.getText();
    const regex = /\[[^\]]+\]/g;
    let matchArr;
    while ((matchArr = regex.exec(text)) !== null) {
        callback(matchArr.index, matchArr.index + matchArr[0].length);
    }
}

const chipDecorator = new CompositeDecorator([
    {
        strategy: findChipEntities,
        component: ChipSpan,
    },
]);

/* ─── EditableCell ───────────────────────────────────────────────────────── */

const EditableCell = ({ value, rowId, colKey, chunkIndex, onInput, onFocus, onBlur }) => {
    const elRef = useRef(null);

    useEffect(() => {
        if (elRef.current && elRef.current.innerText !== (value || '')) {
            elRef.current.innerText = value || '';
        }
    }, [value]);

    return (
        <span
            ref={elRef}
            contentEditable
            suppressContentEditableWarning
            data-row={rowId}
            data-col={colKey}
            data-chunk-index={chunkIndex}
            className="mg-cell-span"
            onInput={onInput}
            onFocus={onFocus}
            onBlur={onBlur}
        />
    );
};

/* ─── MetadataGrid Functional Component ─────────────────────────────────── */

const MetadataGrid = ({
    rows: propRows,
    selectedType,
    selectedLanguage,
    selectedCountry,
    onSaveRow,
    onDeleteRow,
    onRefresh,
    currentPage: propCurrentPage,
    totalRecords: propTotalRecords,
    pageSize: propPageSize,
    onPageChange
}) => {
    console.log(propRows, "propRows");
    console.log(selectedLanguage, "selectedLanguage");
    console.log(selectedType, "selectedType");
    // ── Instance Data ──
    const gridRef = useRef(null);
    const resizeData = useRef(null);
    const canvasRef = useRef(null);
    const chipBlurTimeout = useRef(null);

    // ── Main State ──
    const [rows, setRows] = useState({});
    const [columns, setColumns] = useState([]);
    const [columnWidths, setColumnWidths] = useState({});
    const [savingRowIds, setSavingRowIds] = useState({});
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkSummary, setBulkSummary] = useState(null);

    // ── Filter & Sort State ──
    const [searchField, setSearchField] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ── UI Modal State ──
    const [chipCell, setChipCell] = useState(null);
    const [editorModal, setEditorModal] = useState({
        open: false,
        rowId: null,
        colKey: null,
        editorState: EditorState.createEmpty(chipDecorator),
        focused: false,
    });

    // ── Sync Props to State (Replacement for getDerivedStateFromProps) ──
    useEffect(() => {
        if (!propRows || propRows.length === 0) {
            setRows({});
            setColumns([]);
            setCurrentPage(1);
            return;
        }

        const allKeys = Object.keys(propRows[0]);
        const ordered = [
            ...COL_ORDER.filter(k => allKeys.includes(k)),
            ...allKeys.filter(k => !COL_ORDER.includes(k) && !SKIP_KEYS.includes(k)),
        ];

        setColumns(ordered);

        setRows(prevRows => {
            const nextRows = {};
            propRows.forEach((row, index) => {
                // Use index-based identifier to guarantee all rows are shown regardless of ID collisions
                const rowId = `row-${index}`;

                // Preserve dirty state from previous state if this is the same record
                const existing = prevRows[rowId];
                nextRows[rowId] = {
                    ...row,
                    lang_code: row.SEO_LANG_CODE || row.lang_code || row.LANG_CODE || (selectedLanguage ? selectedLanguage.value : 'en'),
                    seo_active_yn: row.seo_active_yn || row.SEO_ACTIVE_YN || 'Y',
                    _dirty: existing ? existing._dirty : false,
                    _id: rowId
                };
            });
            return nextRows;
        });

        setColumnWidths(prevWidths => {
            const nextWidths = { ...prevWidths };
            ordered.forEach(k => { if (!nextWidths[k]) nextWidths[k] = DEFAULT_COL_WIDTH; });
            return nextWidths;
        });

    }, [propRows]);

    // ── Canvas for Text Measurement ──
    useEffect(() => {
        canvasRef.current = document.createElement('canvas');
    }, []);

    const measureText = useCallback((text, font = '600 12px Inter') => {
        if (!canvasRef.current) return 0;
        const context = canvasRef.current.getContext('2d');
        context.font = font;
        return context.measureText(text).width;
    }, []);

    // ── Filtering Logic ──
    const filteredRowIds = useMemo(() => {
        const allIds = Object.keys(rows);
        if (!searchField || !searchValue.trim()) return allIds;
        const needle = searchValue.trim().toLowerCase();
        return allIds.filter(id => {
            const cellVal = String(rows[id][searchField] || '').toLowerCase();
            return cellVal.includes(needle);
        });
    }, [rows, searchField, searchValue]);

    const isPageType = useMemo(() => {
        return selectedType?.type?.toUpperCase() === 'PAGE' || Object.values(rows).some(r => r.SOURCE_TYPE === 'PAGE_INFO' || r.SOURCE_TYPE === 'HOME_PAGE');
    }, [selectedType, rows]);

    const activeCurrentPage = isPageType ? (propCurrentPage || 1) : currentPage;

    const activeTotalPages = useMemo(() => {
        if (isPageType) {
            return Math.max(1, Math.ceil((propTotalRecords || 0) / PAGE_SIZE));
        }
        return Math.max(1, Math.ceil(filteredRowIds.length / PAGE_SIZE));
    }, [isPageType, propTotalRecords, filteredRowIds.length]);

    const handlePageChange = (newPage) => {
        if (isPageType) {
            if (onPageChange) onPageChange(newPage);
        } else {
            setCurrentPage(newPage);
        }
    };

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(filteredRowIds.length / PAGE_SIZE));
    }, [filteredRowIds.length]);

    const pagedRowIds = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredRowIds.slice(start, start + PAGE_SIZE);
    }, [filteredRowIds, currentPage]);

    const pageInfoRowIds = useMemo(() => {
        if (!isPageType) return [];
        return filteredRowIds.filter(id => rows[id]?.SOURCE_TYPE === 'PAGE_INFO');
    }, [filteredRowIds, rows, isPageType]);

    const homePageRowIds = useMemo(() => {
        if (!isPageType) return [];
        return filteredRowIds.filter(id => rows[id]?.SOURCE_TYPE === 'HOME_PAGE');
    }, [filteredRowIds, rows, isPageType]);

    // Reset pagination on search
    useEffect(() => {
        setCurrentPage(1);
    }, [searchField, searchValue]);

    // ── Column Resize Logic ──
    const handleResizeMoving = useCallback((e) => {
        if (!resizeData.current) return;
        const { startX, initialWidth, colEl } = resizeData.current;

        const delta = e.clientX - startX;
        const newWidth = Math.max(MIN_COL_WIDTH, initialWidth + delta);

        if (colEl) {
            colEl.style.width = `${newWidth}px`;
        }

        resizeData.current.lastWidth = newWidth;
    }, []);

    const handleResizeEnd = useCallback(() => {
        if (!resizeData.current) return;

        const { colKey, lastWidth } = resizeData.current;

        setColumnWidths(prev => ({
            ...prev,
            [colKey]: lastWidth
        }));

        window.removeEventListener('mousemove', handleResizeMoving);
        window.removeEventListener('mouseup', handleResizeEnd);
        document.body.classList.remove('is-resizing');
        resizeData.current = null;
    }, [handleResizeMoving]);

    const handleResizeStart = (e, colKey) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent external drag handlers from intercepting

        const startX = e.clientX;
        const initialWidth = columnWidths[colKey] || DEFAULT_COL_WIDTH;

        // Use closest('table') to avoid gridRef detachment issues caused by extra wrapper components
        const table = e.target.closest('table');
        const colEl = table?.querySelector(`col[data-col="${colKey}"]`);

        resizeData.current = {
            colKey,
            startX,
            initialWidth,
            colEl,
            lastWidth: initialWidth
        };

        // Attach to window instead of document to fix scope/iframe issues
        window.addEventListener('mousemove', handleResizeMoving);
        window.addEventListener('mouseup', handleResizeEnd);
        document.body.classList.add('is-resizing');
    };

    const handleDoubleClick = (colKey) => {
        const label = COL_LABELS[colKey] || colKey;
        let maxWidth = measureText(label, '600 12px Inter') + 30;

        const activeIds = isPageType ? filteredRowIds : pagedRowIds;
        activeIds.forEach(id => {
            const val = String(rows[id][colKey] || '');
            const w = measureText(val, '400 13px Inter') + 20;
            if (w > maxWidth) maxWidth = w;
        });

        maxWidth = Math.max(MIN_COL_WIDTH, Math.ceil(maxWidth));
        setColumnWidths(prev => ({ ...prev, [colKey]: maxWidth }));
    };

    // ── Editing Handlers ──
    const handleCellInput = (e, rowId, colKey) => {
        const value = e.currentTarget.innerText;
        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], [colKey]: value, _dirty: true }
        }));
    };

    const handleChipCellInput = (e, rowId, colKey, chunkIndex) => {
        const row = rows[rowId];
        const currentValue = String(row?.[colKey] || '');
        const chunks = currentValue.split(/(\[[^\]]+\])/g);
        chunks[chunkIndex] = e.currentTarget.innerText;
        const newValue = chunks.join('');
        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], [colKey]: newValue, _dirty: true }
        }));
    };

    const removeChipFromCell = (rowId, colKey, chunkIndex) => {
        const row = rows[rowId];
        const currentValue = String(row?.[colKey] || '');
        const chunks = currentValue.split(/(\[[^\]]+\])/g);
        chunks.splice(chunkIndex, 1);
        const newValue = chunks.join('');
        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], [colKey]: newValue, _dirty: true }
        }));
    };

    const applyChip = (rowId, colKey, chipValue) => {
        if (chipBlurTimeout.current) { clearTimeout(chipBlurTimeout.current); chipBlurTimeout.current = null; }
        const chipLabel = `[${chipValue}]`;

        if (editorModal.open && editorModal.rowId === rowId && editorModal.colKey === colKey) {
            const { editorState } = editorModal;
            const contentState = editorState.getCurrentContent();
            const selection = editorState.getSelection();
            const newContentState = Modifier.insertText(contentState, selection, chipLabel);
            const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
            setEditorModal(prev => ({ ...prev, editorState: newEditorState }));
            return;
        }

        const currentValue = rows[rowId]?.[colKey] || '';
        let insertPos = currentValue.length;
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const container = range.startContainer;
            const editableSpan = (container.nodeType === 3 ? container.parentNode : container);
            if (editableSpan?.getAttribute?.('data-col') === colKey && editableSpan?.getAttribute?.('data-row') === rowId) {
                const chunkIndex = parseInt(editableSpan.getAttribute('data-chunk-index') || '0');
                const chunks = currentValue.split(/(\[[^\]]+\])/g);
                let basePos = 0;
                for (let i = 0; i < chunkIndex; i++) basePos += (chunks[i] ? chunks[i].length : 0);
                insertPos = basePos + range.startOffset;
            }
        }

        const suffix = (currentValue[insertPos] === ' ' ? '' : ' ');
        const newValue = currentValue.slice(0, insertPos) + chipLabel + suffix + currentValue.slice(insertPos);

        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], [colKey]: newValue, _dirty: true }
        }));
        setChipCell(null);

        // Maintain focus
        setTimeout(() => {
            const el = gridRef.current?.querySelector(`[data-row="${rowId}"][data-col="${colKey}"]`);
            if (el) {
                const spans = el.querySelectorAll('.mg-cell-span');
                const last = spans[spans.length - 1];
                if (last) {
                    last.focus();
                    const range = document.createRange();
                    const s = window.getSelection();
                    range.selectNodeContents(last);
                    range.collapse(false);
                    s.removeAllRanges();
                    s.addRange(range);
                }
            }
        }, 50);
    };

    const handleStatusToggle = async (rowId) => {
        const current = rows[rowId]?.seo_active_yn || 'Y';
        const next = current === 'Y' ? 'N' : 'Y';

        // Update local state first for immediate UI feedback
        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], seo_active_yn: next, _dirty: true }
        }));

        // Automatically trigger save for status toggle, passing the 'next' status explicitly
        handleSaveRow(rowId, false, true, next);
    };

    const handleDeleteRow = async (rowId) => {
        const row = rows[rowId];
        if (!row) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete "${row.name || row.seo_page_name || 'this record'}"?`);
        if (!confirmDelete) return;

        setSavingRowIds(prev => ({ ...prev, [rowId]: true }));
        try {
            if (onDeleteRow) {
                const seoSysId = row.seo_sys_id || row.SEO_SYS_ID || row.seo_id || row.SEO_ID || '';
                const language = row.lang_code || (selectedLanguage ? selectedLanguage.value : 'en');
                const success = await onDeleteRow(row.id || row.ID || '', seoSysId, language);
                if (success) {
                    setRows(prev => {
                        const next = { ...prev };
                        delete next[rowId];
                        return next;
                    });
                }
            }
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setSavingRowIds(prev => ({ ...prev, [rowId]: false }));
        }
    };

    // ── Editor Modal ──
    const openEditorModal = (rowId, colKey) => {
        const row = rows[rowId];
        const htmlValue = row?.[colKey] || '';
        let editorState;
        try {
            editorState = htmlValue
                ? EditorState.createWithContent(stateFromHTML(htmlValue), chipDecorator)
                : EditorState.createEmpty(chipDecorator);
        } catch (_) {
            editorState = EditorState.createEmpty(chipDecorator);
        }
        setEditorModal({ open: true, rowId, colKey, editorState, focused: false });
    };

    const applyEditorModal = () => {
        const { rowId, colKey, editorState } = editorModal;
        const htmlValue = stateToHTML(editorState.getCurrentContent());
        setRows(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], [colKey]: htmlValue, _dirty: true }
        }));
        setEditorModal(prev => ({ ...prev, open: false }));
    };

    // ── Save Logic ──
    const handleSaveRow = async (rowId, skipRefresh = false, isStatusOnly = false, overrideStatus = null) => {
        const row = rows[rowId];
        if (!row) return false;

        setSavingRowIds(prev => ({ ...prev, [rowId]: true }));

        try {
            const fmData = new FormData();
            if (!isStatusOnly) {
                const allEditableFields = [...EDITOR_FIELDS, ...CHIP_FIELDS];
                allEditableFields.forEach(f => {
                    let val = row[f] || '';
                    // Remove brackets from chips for the payload: [Value] -> Value
                    if (typeof val === 'string') {
                        val = val.replace(/\[([^\]]+)\]/g, '$1');
                    }
                    fmData.append(f, val);
                });
            }
            console.log(row, "this is row");
            const activeStatus = overrideStatus !== null ? overrideStatus : (row.seo_active_yn || row.SEO_ACTIVE_YN || 'N');
            fmData.append('seo_active_yn', activeStatus);
            fmData.append('seo_page_name', row.seo_page_name || row.name || '');
            const refIdVal = (row.SOURCE_TYPE === 'HOME_PAGE' || row.source_type === 'HOME_PAGE')
                ? (row.SHP_SYS_ID || row.shp_sys_id || '')
                : (row.id || row.ID || '');
            fmData.append('seo_ref_id', refIdVal);
            fmData.append('old_avatar', row.old_avatar || '');
            fmData.append('avatar', '');
            fmData.append('language', row.lang_code || (selectedLanguage ? selectedLanguage.value : 'en'));
            fmData.append('lang_code', row.lang_code || '');
            const countryVal = row.seo_country || selectedCountry?.value || '';
            fmData.append('selectedFlag', countryVal ? String(countryVal).split(',') : []);

            const seoFor = selectedType ? (SEO_FOR_MAP[selectedType.value] || selectedType.value) : '';
            const seoId = refIdVal;
            const seoSysId = row.seo_sys_id || row.SEO_SYS_ID || row.seo_id || row.SEO_ID || '';
            const language = row.lang_code || (selectedLanguage ? selectedLanguage.value : 'en');

            if (onSaveRow) {
                await onSaveRow(fmData, seoId, seoFor, language, seoSysId, skipRefresh);
            }

            setRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], _dirty: false } }));
            setSavingRowIds(prev => ({ ...prev, [rowId]: false }));
            return true;
        } catch (error) {
            setSavingRowIds(prev => ({ ...prev, [rowId]: false }));
            return false;
        }
    };

    const handleSaveAll = async () => {
        const dirtyRowIds = Object.keys(rows).filter(id => rows[id]._dirty);
        if (dirtyRowIds.length === 0) return;

        setBulkSaving(true);
        setBulkSummary(null);
        let updated = 0, created = 0, failed = 0;
        for (const rowId of dirtyRowIds) {
            const success = await handleSaveRow(rowId, true);
            if (success) {
                if (rows[rowId].seo_id || rows[rowId].seo_sys_id) updated++;
                else created++;
            } else failed++;
        }

        if (updated > 0 || created > 0) {
            if (onRefresh) onRefresh();
        }

        setBulkSaving(false);
        setBulkSummary({ updated, created, failed });
        setTimeout(() => setBulkSummary(null), 5000);
    };

    // ── Export ──
    const handleExport = (rowIdsToExport = null) => {
        let ids = rowIdsToExport || filteredRowIds;
        if (isPageType && !rowIdsToExport) {
            const pi = ids.filter(id => rows[id]?.SOURCE_TYPE === 'PAGE_INFO');
            const hp = ids.filter(id => rows[id]?.SOURCE_TYPE === 'HOME_PAGE');
            ids = [...pi, ...hp];
        }
        const exportData = ids.map(id => {
            const row = rows[id];
            const exportRow = {};
            columns.forEach(k => {
                const label = COL_LABELS[k] || k;
                let val = String(row[k] || '').replace(/<[^>]+>/g, '');
                exportRow[label] = val;
            });
            return exportRow;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Metadata");
        XLSX.writeFile(wb, "metadata_export.xlsx");
    };

    // ── Render Helpers ──
    const getChipDataForRow = (rowId) => {
        const row = rows[rowId];
        if (!row) return {};
        const source = row.originalData || row;
        const excluded = [
            'id', 'type', 'label', 'value', 'originalData', 'image_path', 'img_path',
            '_dirty', '_id', 'seo_id', 'lang_code', 'seo_active_yn', 'active_yn',
            'lang_yn', 'SEO_ACTIVE_YN', 'ACTIVE_YN', 'LANG_YN', 'LANG_CODE',
            'seo_country', 'SHP_FILE_PATH', 'SHP_DESC', ...EDITOR_FIELDS
        ];
        const chips = {};
        Object.keys(source).forEach(k => {
            if (!excluded.includes(k) && source[k] && typeof source[k] === 'string') chips[k] = source[k];
        });
        return chips;
    };

    const renderChipPopup = (rowId, inModal = false) => {
        if (!chipCell || chipCell.rowId !== rowId) return null;
        const chips = getChipDataForRow(rowId);
        const keys = Object.keys(chips);
        if (keys.length === 0) return null;

        return (
            <div className={`mg-chip-popup${inModal ? ' mg-chip-popup-modal' : ''}`} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}>
                <div className="mg-chip-popup-header">
                    <span className="mg-chip-popup-title">📎 Suggested Chips</span>
                    <span className="mg-chip-popup-hint">Click to insert</span>
                </div>
                <div className="mg-chip-popup-badges">
                    {keys.map(k => (
                        <div key={k} className="mg-chip-item" onClick={() => applyChip(rowId, chipCell.colKey, chips[k])}>
                            <span className="mg-chip-label">{COL_LABELS[k] || k}:</span>
                            <span className="mg-chip-value">{chips[k]}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderCell = (rowId, colKey, globalIndex) => {
        const row = rows[rowId];
        const value = row?.[colKey] ?? '';
        const isReadOnly = !EDITABLE_FIELDS.includes(colKey);
        const isEditor = EDITOR_FIELDS.includes(colKey);
        const isChip = CHIP_FIELDS.includes(colKey);
        const isChipActive = isChip && !isReadOnly && chipCell?.rowId === rowId && chipCell?.colKey === colKey;

        if (isReadOnly) {
            let cellContent = value;
            if (colKey === 'SHP_FILE_PATH') {
                cellContent = value ? (
                    <img
                        src={value}
                        alt="preview"
                        style={{ maxWidth: "100px", maxHeight: "80px", objectFit: "contain", borderRadius: "4px" }}
                    />
                ) : '';
            }
            return (
                <td key={colKey} data-col={colKey} className="mg-cell mg-cell-readonly">
                    {colKey === 'SHP_FILE_PATH' ? (
                        cellContent
                    ) : (
                        <span className="mg-cell-text" title={String(value)}>{value}</span>
                    )}
                </td>
            );
        }

        if (isEditor) {
            const preview = value ? String(value).replace(/<[^>]+>/g, ' ').trim().slice(0, 70) : '';
            return (
                <td key={colKey} data-col={colKey} className="mg-cell mg-cell-editor" onClick={() => openEditorModal(rowId, colKey)}>
                    <div className="mg-editor-preview">
                        {preview || <span className="mg-placeholder">Click to edit…</span>}
                        <span className="mg-editor-icon">✏️</span>
                    </div>
                </td>
            );
        }

        if (isChip) {
            const chunks = String(value).split(/(\[[^\]]+\])/g);
            return (
                <td
                    key={colKey}
                    data-col={colKey}
                    className={`mg-cell mg-cell-editable mg-cell-chip${isChipActive ? ' mg-cell-chip-active' : ''}`}
                    onClick={(e) => {
                        // Focus the last editable span if clicking inside the cell but outside a tag/button
                        if (!e.target.closest('.mg-cell-chip-tag') && !e.target.closest('.mg-chip-remove')) {
                            const spans = e.currentTarget.querySelectorAll('.mg-cell-span');
                            const lastSpan = spans[spans.length - 1];
                            if (lastSpan) {
                                lastSpan.focus();
                            }
                        }
                    }}
                >
                    <div className="mg-cell-inner">
                        <div className="mg-chip-container">
                            {chunks.map((chunk, idx) => (
                                chunk.startsWith('[') && chunk.endsWith(']') ? (
                                    <span key={idx} className="mg-cell-chip-tag" contentEditable={false}>
                                        {chunk.slice(1, -1)}
                                        <button className="mg-chip-remove" onClick={() => removeChipFromCell(rowId, colKey, idx)}>×</button>
                                    </span>
                                ) : (
                                    <EditableCell
                                        key={idx}
                                        value={chunk}
                                        rowId={rowId}
                                        colKey={colKey}
                                        chunkIndex={idx}
                                        onInput={e => handleChipCellInput(e, rowId, colKey, idx)}
                                        onFocus={() => { if (chipBlurTimeout.current) clearTimeout(chipBlurTimeout.current); setChipCell({ rowId, colKey }); }}
                                        onBlur={() => { chipBlurTimeout.current = setTimeout(() => setChipCell(null), 250); }}
                                    />
                                )
                            ))}
                        </div>
                        {isChipActive && renderChipPopup(rowId)}
                    </div>
                </td>
            );
        }

        return (
            <td
                key={colKey}
                data-col={colKey}
                className={`mg-cell mg-cell-editable`}
                onClick={(e) => {
                    if (!e.target.closest('.mg-cell-span')) {
                        const span = e.currentTarget.querySelector('.mg-cell-span');
                        if (span) span.focus();
                    }
                }}
            >
                <div className="mg-cell-inner">
                    <EditableCell
                        value={String(value)}
                        rowId={rowId}
                        colKey={colKey}
                        onInput={e => handleCellInput(e, rowId, colKey)}
                        onFocus={() => setChipCell(null)}
                    />
                </div>
            </td>
        );
    };

    const renderRow = (rowId, globalIndex) => {
        const row = rows[rowId];
        const isDirty = row?._dirty;
        const isSaving = savingRowIds[rowId];

        return (
            <tr key={rowId} className={`mg-row${isDirty ? ' mg-row-dirty' : ''}${isSaving ? ' mg-row-saving' : ''}`}>
                <td className="mg-cell mg-cell-readonly mg-cell-rownum" data-col="rownum">
                    <span className="mg-rownum">{globalIndex + 1}</span>
                </td>
                {columns.map(colKey => renderCell(rowId, colKey, globalIndex))}
                <td className="mg-cell mg-cell-action" data-col="action">
                    <div className="mg-action-group">
                        <button
                            className={`mg-status-btn mg-status-btn-${row?.seo_active_yn === 'Y' ? 'active' : 'inactive'}`}
                            onClick={() => handleStatusToggle(rowId)}
                            title={row?.seo_active_yn === 'Y' ? 'Deactivate' : 'Activate'}
                        >
                            {row?.seo_active_yn === 'Y' ? '🟢' : '⚪'}
                        </button>
                        <button
                            className={`mg-save-btn${isDirty ? ' mg-save-btn-active' : ''}${isSaving ? ' mg-save-btn-loading' : ''}`}
                            onClick={() => handleSaveRow(rowId)}
                            disabled={isSaving || !isDirty}
                        >
                            {isSaving ? <span className="mg-spinner-small" /> : isDirty ? '💾 Save' : '✓'}
                        </button>
                        <button
                            className="mg-delete-btn"
                            onClick={() => handleDeleteRow(rowId)}
                            disabled={isSaving}
                            title="Delete"
                        >
                            🗑️
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    const searchableCols = columns.filter(k => !['image_path', 'img_path', 'seo_active_yn', 'seo_country', 'SHP_FILE_PATH'].includes(k));

    const renderTableMarkup = (rowIds, title, startIdx = 0) => {
        return (
            <div className="mg-table-wrapper-section" style={{ marginBottom: title ? '24px' : '0' }}>
                {title && (
                    <div className="mg-table-section-header">
                        <span className="mg-table-section-title">{title}</span>
                        <Badge bg="secondary" className="ms-2">{rowIds.length}</Badge>
                    </div>
                )}
                <table className="mg-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                    <colgroup>
                        <col data-col="rownum" style={{ width: '44px' }} />
                        {columns.map(k => (
                            <col
                                key={k}
                                data-col={k}
                                style={{ width: `${columnWidths[k] || DEFAULT_COL_WIDTH}px` }}
                            />
                        ))}
                        <col data-col="action" style={{ width: '130px' }} />
                    </colgroup>
                    <thead>
                        <tr className="mg-header-row">
                            <th className="mg-th mg-th-rownum" style={{ width: '44px' }}>#</th>
                            {columns.map(k => (
                                <th
                                    key={k}
                                    className="mg-th"
                                    style={{
                                        minWidth: MIN_COL_WIDTH,
                                        width: `${columnWidths[k] || DEFAULT_COL_WIDTH}px`
                                    }}
                                >
                                    <div className="mg-th-content">
                                        <span className="mg-th-label">{COL_LABELS[k] || k}</span>
                                        {EDITOR_FIELDS.includes(k) && <span className="mg-th-badge mg-th-badge-editor">✏</span>}
                                        {CHIP_FIELDS.includes(k) && <span className="mg-th-badge mg-th-badge-chip">◈</span>}
                                    </div>
                                    <div
                                        className="mg-resize-handle"
                                        onMouseDown={(e) => handleResizeStart(e, k)}
                                        onDoubleClick={() => handleDoubleClick(k)}
                                        title="Drag to resize / Double-click to auto-fit"
                                    />
                                </th>
                            ))}
                            <th className="mg-th mg-th-action" style={{ width: '130px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rowIds.map((id, index) => renderRow(id, startIdx + index))}
                        {rowIds.length === 0 && (
                            <tr><td colSpan={columns.length + 2} className="mg-empty-row">No records found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="mg-container">
            {/* WYSIWYG Modal */}
            <Modal show={editorModal.open} onHide={() => setEditorModal(p => ({ ...p, open: false }))} size="lg" dialogClassName="mg-editor-modal">
                <Modal.Header closeButton>
                    <Modal.Title>✏️ {editorModal.colKey ? (COL_LABELS[editorModal.colKey] || editorModal.colKey) : 'Edit Content'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 position-relative">
                    <Editor
                        editorState={editorModal.editorState}
                        toolbarClassName="mg-editor-toolbar"
                        wrapperClassName="mg-editor-wrapper"
                        editorClassName="mg-editor-body"
                        onEditorStateChange={state => setEditorModal(p => ({ ...p, editorState: state }))}
                        onFocus={() => { setEditorModal(p => ({ ...p, focused: true })); setChipCell({ rowId: editorModal.rowId, colKey: editorModal.colKey }); }}
                        onBlur={() => { setEditorModal(p => ({ ...p, focused: false })); chipBlurTimeout.current = setTimeout(() => setChipCell(null), 250); }}
                    />
                    {editorModal.open && editorModal.focused && renderChipPopup(editorModal.rowId, true)}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditorModal(p => ({ ...p, open: false }))}>Cancel</Button>
                    <Button variant="success" onClick={applyEditorModal}>✓ Apply</Button>
                </Modal.Footer>
            </Modal>

            {/* Toolbar */}
            <div className="mg-toolbar">
                <div className="mg-toolbar-left">
                    <span className="mg-toolbar-label">Search in:</span>
                    <select className="mg-select" value={searchField} onChange={(e) => setSearchField(e.target.value)}>
                        <option value="">— Select Column —</option>
                        {searchableCols.map(k => <option key={k} value={k}>{COL_LABELS[k] || k}</option>)}
                    </select>
                    <div className="mg-search-input-wrapper">
                        <input
                            className="mg-search-input"
                            placeholder={searchField ? `Search ${COL_LABELS[searchField] || searchField}…` : 'Select a column...'}
                            value={searchValue}
                            disabled={!searchField}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                        {searchValue && <button className="mg-btn-clear-inline" onClick={() => setSearchValue('')}>×</button>}
                    </div>
                </div>

                {bulkSummary && (
                    <div className="mg-bulk-summary">
                        <span className="mg-summary-item mg-summary-updated">Updated: {bulkSummary.updated}</span>
                        <span className="mg-summary-item mg-summary-created">Created: {bulkSummary.created}</span>
                        <span className="mg-summary-item mg-summary-failed">Failed: {bulkSummary.failed}</span>
                    </div>
                )}

                <div className="mg-toolbar-right">
                    <button
                        className={`mg-btn-bulk-save ${Object.keys(rows).some(id => rows[id]._dirty) ? 'active' : ''}`}
                        onClick={handleSaveAll}
                        disabled={bulkSaving || !Object.keys(rows).some(id => rows[id]._dirty)}
                    >
                        {bulkSaving ? <><span className="mg-spinner-small" style={{ marginRight: 8 }} /> Saving...</> : <><span className="mg-btn-icon">💾</span> Save All</>}
                    </button>
                    <button className="mg-btn-export" onClick={() => handleExport()}>
                        <span className="mg-btn-icon">📤</span> Export Filtered
                    </button>
                </div>
            </div>

            {/* Grid */}
            <div className="mg-scroll-wrapper" ref={gridRef}>
                {isPageType ? (
                    <>
                        {renderTableMarkup(pageInfoRowIds, 'Page Info', 0)}
                        {renderTableMarkup(homePageRowIds, 'Home Page Info', (activeCurrentPage - 1) * PAGE_SIZE)}
                    </>
                ) : (
                    renderTableMarkup(pagedRowIds, null, (activeCurrentPage - 1) * PAGE_SIZE)
                )}
            </div>

            {/* Pagination */}
            {activeTotalPages > 1 && (
                <div className="mg-pagination-modern">
                    <div className="mg-pagination-info">
                        Page <strong>{activeCurrentPage}</strong> of <strong>{activeTotalPages}</strong> ({isPageType ? propTotalRecords : filteredRowIds.length} records)
                    </div>
                    <div className="mg-pagination-controls">
                        <button className="mg-page-btn" disabled={activeCurrentPage === 1} onClick={() => handlePageChange(activeCurrentPage - 1)}>‹ Prev</button>
                        {Array.from({ length: activeTotalPages }, (_, i) => i + 1).filter(p => p === 1 || p === activeTotalPages || (p >= activeCurrentPage - 2 && p <= activeCurrentPage + 2)).map((p, i, arr) => (
                            <React.Fragment key={p}>
                                {i > 0 && p > arr[i - 1] + 1 && <span className="mg-page-ellipsis">…</span>}
                                <button className={`mg-page-btn ${activeCurrentPage === p ? 'active' : ''}`} onClick={() => handlePageChange(p)}>{p} </button>
                            </React.Fragment>
                        ))}
                        <button className="mg-page-btn" disabled={activeCurrentPage === activeTotalPages} onClick={() => handlePageChange(activeCurrentPage + 1)}>Next ›</button>
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="mg-legend">
                <span className="mg-legend-item"><span className="mg-th-badge mg-th-badge-editor">✏</span> Editor</span>
                <span className="mg-legend-item"><span className="mg-th-badge mg-th-badge-chip">◈</span> Chips</span>
                <span className="mg-legend-item mg-legend-dirty"><span className="mg-dirty-dot" /> Unsaved</span>
            </div>
        </div>
    );
};

export default MetadataGrid;
